import { Box, Button, Typography, Paper, Alert } from "@mui/material";
import InfoHeader from "../components/InfoHeader";
import SwotTable from "../components/SwotTable";
import React, { useEffect, useState } from "react";
import { swotService } from "../services/SwotService";
import Swot from "../types/negocio/Swot";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import toastService from "../utils/toastService";
import LoadingIndicator from '../components/LoadingIndicator';
import { YouTube, TrendingUp, TrendingDown } from "@mui/icons-material";
import BasicModal from '../components/modais/BasicModal';
import ReactPlayer from 'react-player';
import { PrimaryButton } from "../components";


const SwotPage: React.FC = () => {
    const [pontosFortes, setPontosFortes] = React.useState<Swot[]>([]);
    const [oportunidades, setOportunidades] = React.useState<Swot[]>([]);
    const [pontosFracos, setPontosFracos] = React.useState<Swot[]>([]);
    const [ameacas, setAmeacas] = React.useState<Swot[]>([]);
    const [loading, setLoading] = useState(true);
    const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
    const [cenarioAtual, setCenarioAtual] = useState<"favoravel" | "desfavoravel" | null>(null);
    const [cenarioDetalhes, setCenarioDetalhes] = useState({
        somaSO: 0,
        somaWT: 0
    });

    const [expandedQuadrants, setExpandedQuadrants] = useState({
        S: false,
        W: false,
        O: false,
        T: false
    });

    // Toggle expanded state for a quadrant
    const toggleExpand = (quadrant: 'S' | 'W' | 'O' | 'T') => {
        setExpandedQuadrants(prev => ({
            ...prev,
            [quadrant]: !prev[quadrant]
        }));
    };

    // Get items based on expansion state
    const getVisibleItems = (items: Swot[], quadrant: 'S' | 'W' | 'O' | 'T') => {
        return expandedQuadrants[quadrant] ? items : items.slice(0, 5);
    };

    // Calculate total score for a quadrant
    const calculateTotalScore = (items: Swot[]): number => {
        const visibleItems: Swot[] = items.slice(0, 5);
        return visibleItems.reduce((total, item) => total + (item.pontuacao || 0), 0);
    };

    const getCenario = (pontosFortes: Swot[], pontosFracos: Swot[], oportunidades: Swot[], ameacas: Swot[]): void => {
        const somaSO = calculateTotalScore(pontosFortes) + calculateTotalScore(oportunidades);
        const somaWT = calculateTotalScore(pontosFracos) + calculateTotalScore(ameacas);

        setCenarioDetalhes({ somaSO, somaWT });

        if (somaSO >= somaWT) {
            setCenarioAtual("favoravel");
        } else {
            setCenarioAtual("desfavoravel");
        }
    };

    useEffect(() => {
        setLoading(true);
        swotService.obterQuadrante().then((data) => {
            setPontosFortes(data.pontosFortes);
            setPontosFracos(data.pontosFracos);
            setOportunidades(data.oportunidades);
            setAmeacas(data.ameacas);
            setLoading(false);
            getCenario(data.pontosFortes, data.pontosFracos, data.oportunidades, data.ameacas);
        }).catch((error) => {
            console.error("Erro ao carregar os dados do SWOT:", error);
            toastService.error("Erro ao carregar os dados do SWOT");
            setLoading(false);
        });
    }, []);

    if (loading) {
        return <LoadingIndicator fullHeight message="Carregando dados SWOT..." />;
    }

    return (
        <Box>
            <InfoHeader
                title="Swot"
                message="Analise os 5 pontos que mais pontuaram em cada quadrante e defina ações para cada um deles."
                isImportant
                url='https://youtu.be/VpbOJqcrgjk'
                modalTitle='Entenda a aba SWOT'
            />

            {cenarioAtual && (
                <Paper
                    elevation={3}
                    sx={{
                        p: 2,
                        mb: 3,
                        display: 'flex',
                        alignItems: 'center',
                        backgroundColor: cenarioAtual === "favoravel" ? 'rgba(46, 125, 50, 0.1)' : 'rgba(211, 47, 47, 0.1)',
                        borderLeft: cenarioAtual === "favoravel" ? '5px solid #2e7d32' : '5px solid #d32f2f',
                    }}
                >
                    {cenarioAtual === "favoravel" ? (
                        <TrendingUp sx={{ color: '#2e7d32', fontSize: 32, mr: 3 }} />
                    ) : (
                        <TrendingDown sx={{ color: '#d32f2f', fontSize: 32, mr: 3 }} />
                    )}
                    <Box>
                        <Typography variant="h6" color={cenarioAtual === "favoravel" ? 'success.main' : 'error.main'} fontWeight="bold">
                            Cenário {cenarioAtual === "favoravel" ? "FAVORÁVEL" : "DESFAVORÁVEL"}
                        </Typography>
                        <Typography variant="body1">
                            {cenarioAtual === "favoravel"
                                ? "A soma de pontos fortes e oportunidades é MAIOR que a soma de pontos fracos e ameaças."
                                : "A soma de pontos fortes e oportunidades é MENOR que a soma de pontos fracos e ameaças."
                            }
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 0.5 }}>
                            Pontos Fortes + Oportunidades: <strong>{cenarioDetalhes.somaSO}</strong> |
                            Pontos Fracos + Ameaças: <strong>{cenarioDetalhes.somaWT}</strong>
                        </Typography>
                        <PrimaryButton
                            variant="contained"
                            sx={{ mt: 1 }}
                            size="small"
                            onClick={() => setIsVideoModalOpen(true)}
                        >
                            Entenda seu cenário
                        </PrimaryButton>
                    </Box>
                </Paper>
            )}


            <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                <Box sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    flexDirection: { xs: 'column', md: 'row' },
                    gap: { xs: 3, md: 0 }
                }}>
                    <Box sx={{ width: { xs: '100%', md: '49%' }, display: 'flex', flexDirection: 'column' }}>
                        <Box sx={{ mb: 1 }}>
                            <Typography variant="subtitle1" fontWeight="bold" color="primary">
                                Pontos Fortes - Pontuação Total: {calculateTotalScore(pontosFortes)}
                            </Typography>
                        </Box>
                        <SwotTable rows={getVisibleItems(pontosFortes, 'S')} title="Pontos Fortes" color="primary" />
                        {pontosFortes.length > 5 && (
                            <PrimaryButton
                                variant="text"
                                size="small"
                                onClick={() => toggleExpand('S')}
                                endIcon={expandedQuadrants.S ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                sx={{ mt: 1, alignSelf: 'center' }}
                            >
                                {expandedQuadrants.S ? 'Ver menos' : 'Ver mais'}
                            </PrimaryButton>
                        )}
                    </Box>
                    <Box sx={{ width: { xs: '100%', md: '49%' }, display: 'flex', flexDirection: 'column' }}>
                        <Box sx={{ mb: 1 }}>
                            <Typography variant="subtitle1" fontWeight="bold" color="primary">
                                Oportunidades - Pontuação Total: {calculateTotalScore(oportunidades)}
                            </Typography>
                        </Box>
                        <SwotTable rows={getVisibleItems(oportunidades, 'O')} title="Oportunidades" color="primary" />
                        {oportunidades.length > 5 && (
                            <PrimaryButton
                                variant="text"
                                size="small"
                                onClick={() => toggleExpand('O')}
                                endIcon={expandedQuadrants.O ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                sx={{ mt: 1, alignSelf: 'center' }}
                            >
                                {expandedQuadrants.O ? 'Ver menos' : 'Ver mais'}
                            </PrimaryButton>
                        )}
                    </Box>
                </Box>
                <Box sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    flexDirection: { xs: 'column', md: 'row' },
                    gap: { xs: 3, md: 0 }
                }}>
                    <Box sx={{ width: { xs: '100%', md: '49%' }, display: 'flex', flexDirection: 'column' }}>
                        <Box sx={{ mb: 1 }}>
                            <Typography variant="subtitle1" fontWeight="bold" color="secondary">
                                Pontos Fracos - Pontuação Total: {calculateTotalScore(pontosFracos)}
                            </Typography>
                        </Box>
                        <SwotTable rows={getVisibleItems(pontosFracos, 'W')} title="Pontos Fracos" color="secondary" />
                        {pontosFracos.length > 5 && (
                            <PrimaryButton
                                variant="text"
                                size="small"
                                onClick={() => toggleExpand('W')}
                                endIcon={expandedQuadrants.W ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                sx={{ mt: 1, alignSelf: 'center' }}
                            >
                                {expandedQuadrants.W ? 'Ver menos' : 'Ver mais'}
                            </PrimaryButton>
                        )}
                    </Box>
                    <Box sx={{ width: { xs: '100%', md: '49%' }, display: 'flex', flexDirection: 'column' }}>
                        <Box sx={{ mb: 1 }}>
                            <Typography variant="subtitle1" fontWeight="bold" color="secondary">
                                Ameaças - Pontuação Total: {calculateTotalScore(ameacas)}
                            </Typography>
                        </Box>
                        <SwotTable rows={getVisibleItems(ameacas, 'T')} title="Ameaças" color="secondary" />
                        {ameacas.length > 5 && (
                            <PrimaryButton
                                variant="text"
                                size="small"
                                onClick={() => toggleExpand('T')}
                                endIcon={expandedQuadrants.T ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                sx={{ mt: 1, alignSelf: 'center' }}
                            >
                                {expandedQuadrants.T ? 'Ver menos' : 'Ver mais'}
                            </PrimaryButton>
                        )}
                    </Box>
                </Box>
            </Box>

            <Alert severity="info" sx={{ mt: 3 }}>
                Pontuação total somente leva em consideração os 5 campos mais pontuados.
            </Alert>

            <BasicModal
                title="Entenda o seu Cenário SWOT"
                open={isVideoModalOpen}
                onClose={() => setIsVideoModalOpen(false)}
                sizeContent="medium"
            >
                <Box sx={{ position: 'relative', paddingTop: '56.2%' }}>
                    {cenarioAtual === "favoravel" ? (
                        // Substituir pelo vídeo de cenário FAVORÁVEL
                        <ReactPlayer
                            url='https://www.youtube.com/watch?v=skg-29jvfsU'
                            width='100%'
                            height='100%'
                            style={{ position: 'absolute', top: 0, left: 0 }}
                            controls
                        />
                    ) : (
                        // Substituir pelo vídeo de cenário DESFAVORÁVEL
                        <ReactPlayer
                            url='https://www.youtube.com/watch?v=skg-29jvfsU'
                            width='100%'
                            height='100%'
                            style={{ position: 'absolute', top: 0, left: 0 }}
                            controls
                        />
                    )}
                </Box>
            </BasicModal>
        </Box>
    )
}

export default SwotPage;
